// Item-level mutations, per the M1 execution plan §5.1 (owner WS-B). Kept in
// its own file, separate from the read-only `useItems.ts` (WS-0), so no file
// is shared between workstreams (UX execution plan §8, WS-0 step 10).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';
import { accountSetInvalidationQueryKeys } from './useExchangePublicToken';
import { rulesQueryKey } from './useRules';
import { lineItemsQueryKey } from './useTypeSuggestions';

export const removeItemResponseSchema = z.object({
  item_id: z.string(),
  removed: z.literal(true),
});

export const removeAccountResponseSchema = z.object({
  account_id: z.string(),
  removed: z.literal(true),
  already_removed: z.boolean(),
});

export const removeItemAccountsResponseSchema = z.object({
  item_id: z.string(),
  removed_account_ids: z.array(z.string()),
  already_removed_account_ids: z.array(z.string()),
});

export const updateCompleteResponseSchema = z.object({
  item_id: z.string(),
  job_id: z.string(),
  bootstrap_status: z.enum(['queued', 'running', 'complete', 'failed']),
});

export type UpdateCompleteResponse = z.infer<typeof updateCompleteResponseSchema>;

const accountReadQueryKeys = [
  ['accounts'],
  ['transactions'],
  ['plaid', 'items'],
  ['networth'],
  ['business-year'],
  ['sync'],
  ['spend-by-category'],
  ['spend-by-merchant'],
  ['inbox'],
  ['reconciliation'],
  [...rulesQueryKey],
  [...lineItemsQueryKey],
  ...accountSetInvalidationQueryKeys.filter(([root]) => root === 'spend' || root === 'sync-runs'),
] as const;

const invalidateAccountReads = (queryClient: ReturnType<typeof useQueryClient>) => {
  for (const queryKey of accountReadQueryKeys) {
    queryClient.invalidateQueries({ queryKey: [...queryKey] });
  }
};

export function accountRemovalErrorMessage(error: unknown): string {
  const generic = 'Could not confirm whether the account was removed. Refresh to check before trying again.';
  if (typeof error !== 'object' || error === null || !('response' in error)) return generic;
  const payload = (error as { response?: { data?: unknown } }).response?.data;
  if (!isErrorEnvelope(payload) || payload.error.code !== 'account_not_removable') return generic;
  return payload.error.message;
}

export function useRemoveAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => {
      const response = await client.post(`/api/accounts/${accountId}/remove`, {
        account_id: accountId,
        confirmation: 'remove_account',
      });
      return parseEnvelope(removeAccountResponseSchema, response.data).data;
    },
    onSuccess: () => invalidateAccountReads(queryClient),
  });
}

export function useRemoveItemAccounts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const response = await client.post(`/api/plaid/items/${itemId}/remove-accounts`, {
        item_id: itemId,
        confirmation: 'remove_all_accounts',
      });
      return parseEnvelope(removeItemAccountsResponseSchema, response.data).data;
    },
    onSuccess: () => invalidateAccountReads(queryClient),
  });
}

/**
 * DELETE /api/plaid/items/{item_id} -- a purge, not a soft hide. Per the plan
 * (WS-3 step 5), the caller states the real consequence before calling this
 * (transactions stay, labelled as of the last sync); this hook does not
 * fabricate a transaction count of its own.
 */
export function useRemoveItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: string) => {
      const response = await client.delete(`/api/plaid/items/${itemId}`, {
        data: { confirmation: 'remove_connection', item_id: itemId },
      });
      return parseEnvelope(removeItemResponseSchema, response.data).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plaid', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/** Records Link update success and queues a sync without exchanging its public token. */
export function useUpdateComplete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, linkSessionId }: { itemId: string; linkSessionId: string }): Promise<UpdateCompleteResponse> => {
      const response = await client.post(`/api/plaid/items/${itemId}/update-complete`, { link_session_id: linkSessionId });
      return parseEnvelope(updateCompleteResponseSchema, response.data).data;
    },
    onSuccess: () => ['plaid', 'items', 'accounts', 'transactions', 'spend', 'sync-runs'].forEach((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] })),
  });
}
